import { ApiError, api } from '../../lib/api'
import { download } from '../../lib/download'
import { isEnglish } from '../../lib/i18n'

/**
 * One user, everything known about them — `Features/Admin/UserDetailsAdmin/Endpoint.cs`.
 *
 *   getUser             -> GET  /api/user-details?username= | ?Id=
 *   listActivities      -> POST /UserDetailsAdmin/GetUserActivities
 *   listStatusLog       -> POST /UserDetailsAdmin/GetUserStatusLog
 *   listPolicies        -> POST /UserDetailsAdmin/GetUserPolicies
 *   listPrograms        -> POST /UserDetailsAdmin/GetUserPrograms
 *   listProgramCourses  -> POST /UserDetailsAdmin/GetCoursesDetails   &programId=
 *   listCourseExams     -> POST /UserDetailsAdmin/GetExamsDetails     &courseId=
 *   listExamTries       -> POST /UserDetailsAdmin/GetExamTries
 *   resetExamTry        -> POST /UserDetailsAdmin/ResetExamTry        &examId=
 *   listAssessments     -> POST /UserDetailsAdmin/GetAssessments
 *   listSurveys         -> POST /UserDetailsAdmin/GetSurveys
 *   listPhishing        -> POST /UserDetailsAdmin/GetPhishingData
 *   listSecurityReports -> POST /UserDetailsAdmin/GetSecurityReports
 *   listPointsLog       -> POST /UserDetailsAdmin/GetUserPointsLog
 *   getProgramsSummary  -> POST /UserDetailsAdmin/GetUserDashboard
 *   sendEmail           -> POST /UserDetailsAdmin/SendEmail
 *   exportData          -> GET  /UserDetailsAdmin/Export
 *   downloadSurveyReport-> GET  /UserDetailsAdmin/DownloadSurveyReport
 *
 * TWO THINGS ABOUT THIS SLICE ARE NOT LIKE THE OTHERS.
 *
 * 1. THE PARAMETERS GO IN THE QUERY STRING, ON A POST. The handlers take primitives
 *    (`Guid userId`, `int examId`) with no `[FromBody]`, and minimal APIs bind primitives
 *    from the query. A JSON body is silently ignored and `userId` arrives as an empty Guid.
 *
 * 2. EVERYTHING IS KEYED BY THE USER'S GUID, which no list in the product hands out — the
 *    rows carry a username. `getUser` is what bridges the two, and every reader below wants
 *    the `userId` it returns, NOT the username in the route. See `API_FINDINGS.md` §27.
 */

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `{ Result, DataSet2 }` or `{ Result, Data }` — the slice uses both names for the body. */
async function post(path, params = {}, signal) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
  )

  const body = await api.post(`/UserDetailsAdmin/${path}?${query}`, undefined, { signal })
  return body?.DataSet2 ?? body?.Data ?? []
}

/** Every tab reader answers this shape; the tab components take it as-is. */
const listed = (rows) => ({ rows, total: rows.length })

/**
 * The page payload, plus the points the header shows.
 *
 * `GetUserPoints` is a second call because the page view model has no points on it, and it
 * needs the Guid the first call returns — so these are sequential, not parallel.
 *
 * @param {string} idOrUsername the route param: a username normally, a Guid when arriving
 *        from a gamification link.
 */
export async function getUser(idOrUsername, signal) {
  const key = GUID.test(idOrUsername) ? 'Id' : 'username'
  const dto = await api.get(`/api/user-details?${key}=${encodeURIComponent(idOrUsername)}`, { signal })

  // The handler answers a view model with `Error` set rather than a status code.
  if (!dto || dto.Error) return null

  const profile = dto.ProfileEntries ?? {}
  const userId = dto.UserId

  const points = await readPoints(userId, signal)

  return {
    userId,
    id: profile.Username ?? idOrUsername,
    userName: profile.Username ?? '',
    name: label(profile.FullNameArabic, profile.FullNameEnglish),
    nameEn: profile.FullNameEnglish ?? '',
    email: profile.Email ?? '',
    department: points.department || profile.Department || '',
    startedOn: profile.StartedOn ?? '',
    lastLogin: dto.LastLogin ?? '',
    /* A LIST of badges, which is what the identity band renders — `UserState` is one
       already-localized string ("نشط"، "موقوف"), so it is that one badge. Returning the
       bare string here blanks the whole screen: the band calls `.map` on it. */
    status: profile.UserState ? [{ label: profile.UserState, color: 'dark' }] : [],
    /* The view model reports this two ways — a flag for the banner, a string for the
       field. The banner is the one the screen acts on. */
    profileUpdated: !dto.ProfileNotUpdated,
    loginProvider: profile.LoginProvider ?? '',
    userManualViewed: profile.UserManualViewed ?? '',
    termsOfUseAgreed: profile.TermsOfUseAgreed ?? '',
    attributes: [profile.Attribute1, profile.Attribute2, profile.Attribute3, profile.Attribute4, profile.Attribute5]
      .map((value) => value ?? ''),
    /* Which tabs the licence and settings actually allow. The screen hides the rest
       rather than showing a tab that can only ever be empty. */
    features: {
      securityReports: Boolean(dto.IsSecurityReportsEnabled),
      phishing: Boolean(dto.IsPhishingEnabled),
      assessments: Boolean(dto.IsAssessmentEnabled),
      surveys: Boolean(dto.IsSurveyEnabled),
      gamification: Boolean(dto.IsGamificationEnabled),
    },
    ...points.summary,
  }
}

/**
 * Points, badges and risk band. Gamification can be switched off, and the endpoint
 * answers a failure rather than zeroes when it is — which is not something to show as an
 * error on a screen whose other twelve tabs work.
 */
async function readPoints(userId, signal) {
  try {
    const rows = await post('GetUserPoints', { userId }, signal)
    const dto = Array.isArray(rows) ? rows[0] : rows

    if (!dto) return { department: '', summary: {} }

    return {
      department: dto.Department ?? '',
      summary: {
        pointsPositive: dto.PointsPositiveTotal ?? 0,
        pointsNegative: dto.PointsNegativeTotal ?? 0,
        pointsRiskLevelName: dto.RiskLevel ?? null,
        badges: (dto.Badges ?? []).map((badge) => ({
          id: badge.Id,
          name: badge.Name ?? '',
          points: badge.Points ?? 0,
          /* The endpoint returns the ladder, not only what was won; a badge is held once
             the positive total reaches it. */
          earned: (dto.PointsPositiveTotal ?? 0) >= (badge.Points ?? 0),
        })),
      },
    }
  } catch (error) {
    if (error instanceof ApiError) return { department: '', summary: {} }
    throw error
  }
}

export async function listActivities(userId, signal) {
  const rows = await post('GetUserActivities', { userId }, signal)
  return listed(
    rows.map((row) => ({
      id: `${row.Date}|${row.Name}`,
      name: row.Name ?? '',
      date: row.Date ?? '',
      details: row.Details ?? '',
    })),
  )
}

export async function listStatusLog(userId, signal) {
  const rows = await post('GetUserStatusLog', { userId }, signal)
  return listed(
    rows.map((row, index) => ({
      id: `${row.Timestamp}|${index}`,
      status: row.Status ?? '',
      date: row.Timestamp ?? '',
      notes: row.Notes ?? '',
    })),
  )
}

export async function listPolicies(userId, signal) {
  const rows = await post('GetUserPolicies', { userId }, signal)
  return listed(
    rows.map((row, index) => ({
      id: `${row.Title}|${index}`,
      title: row.Title ?? '',
      isDownloaded: row.IsDownloaded ?? '',
      date: row.DownloadDate ?? '',
      /* Already localized server-side ("وافق" / "لم يوافق"), so it is shown as received
         rather than mapped through a lookup here (W6). */
      accepted: row.Status ?? '',
      statusDate: row.StatusDate ?? '',
    })),
  )
}

export async function listPrograms(userId, signal) {
  const rows = await post('GetUserPrograms', { userId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.Id,
      name: row.Title ?? '',
      state: row.Status ?? '',
    })),
  )
}

export async function listProgramCourses(userId, programId, signal) {
  const rows = await post('GetCoursesDetails', { userId, programId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.Id,
      name: row.Title ?? '',
      startDate: row.StartDate ?? '',
      endDate: row.EndDate ?? '',
      state: row.Status ?? '',
      percentage: row.ExamPercentage ?? '',
      tries: row.ExamTries ?? '',
      watchPercentage: row.WatchStatus ?? '',
    })),
  )
}

export async function listCourseExams(userId, courseId, signal) {
  const rows = await post('GetExamsDetails', { userId, courseId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.ExamId,
      examId: row.ExamId,
      name: row.ExamName ?? '',
      course: row.CourseName ?? '',
      date: row.ExamDate ?? '',
      /* The table renders `${row.percentage}%`; `Degree` is that number. */
      percentage: row.Degree ?? '',
      state: row.Status ?? '',
    })),
  )
}

export async function listExamTries(userId, signal) {
  const rows = await post('GetExamTries', { userId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.ExamId,
      examId: row.ExamId,
      name: row.ExamName ?? '',
      allowed: row.AllowedTries ?? 0,
      used: row.UserTries ?? 0,
    })),
  )
}

/** Gives the user another attempt at one exam. */
export async function resetExamTry(userId, examId) {
  try {
    await post('ResetExamTry', { userId, examId })
    return { ok: true }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function listAssessments(userId, signal) {
  const rows = await post('GetAssessments', { userId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.Id,
      title: row.Title ?? '',
      // Localized server-side; both are shown as received.
      mandatory: row.IsMandatory ?? '',
      status: row.Status ?? '',
      score: row.Score ?? '',
      completed: row.CompletionDate ?? '',
    })),
  )
}

export async function listSurveys(userId, signal) {
  const rows = await post('GetSurveys', { userId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.SurveyId,
      surveyId: row.SurveyId,
      title: row.Title ?? '',
      createdOn: row.CreationDate ?? '',
      mandatory: row.IsMandatory ?? '',
      status: row.Status ?? '',
      answered: Boolean(row.IsAnswered),
    })),
  )
}

export async function listPhishing(userId, signal) {
  const rows = await post('GetPhishingData', { userId }, signal)
  return listed(
    /* `name` and `status`, which is what the table reads — it emitted `template` and
       `state`, so both columns rendered blank. `Status` arrives already localized. */
    rows.map((row) => ({
      id: row.CampaignId,
      name: row.Name ?? '',
      status: row.Status ?? '',
      reportedOn: row.ReportingDate ?? '',
    })),
  )
}

export async function listSecurityReports(userId, signal) {
  const rows = await post('GetSecurityReports', { userId }, signal)
  return listed(
    rows.map((row) => ({
      id: row.Id,
      title: row.Title ?? '',
      category: row.Category ?? '',
      date: row.CreationDate ?? '',
      state: row.Status ?? '',
      description: row.Content ?? '',
    })),
  )
}

export async function listPointsLog(userId, signal) {
  const rows = await post('GetUserPointsLog', { userId }, signal)
  return listed(
    rows.map((row, index) => ({
      id: `${row.Timestamp}|${index}`,
      activity: row.EventType ?? '',
      date: row.Timestamp ?? '',
      points: row.Points ?? 0,
      /* `PointsType` names the direction; `EffectOpposite` says the entry ran against it
         (a positive rule that cost points, or the reverse). */
      isPositive: (row.Points ?? 0) >= 0,
      description: row.PointsType ?? '',
      effectOpposite: row.EffectOpposite ?? '',
      percentageOnDuplication: row.DuplicationPercentage ?? '',
      resetDate: row.ResetDate ?? '',
    })),
  )
}

/** The four figures above the programs table. */
export async function getProgramsSummary(userId, signal) {
  const rows = await post('GetUserDashboard', { userId }, signal)
  const dto = Array.isArray(rows) ? rows[0] : rows

  return {
    totalPrograms: dto?.TotalProgramsCount ?? 0,
    totalCourses: dto?.TotalCoursesCount ?? 0,
    successProgramsPercentage: dto?.SuccessProgramsPercentage ?? 0,
    successCoursesPercentage: dto?.SuccessCoursesPercentage ?? 0,
    coursesWatchPercentage: dto?.CoursesWatchPercentage ?? 0,
  }
}

export async function sendEmail({ userId, title, body, sendTime }) {
  try {
    await post('SendEmail', {
      userId,
      title: title ?? '',
      body: body ?? '',
      sendTime: sendTime ?? new Date().toISOString(),
    })
    return { ok: true }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * @param {string} tableType which tab's table to export — the server rejects anything it
 *        does not know with a 400.
 * @param {'excel'|'pdf'|'doc'} format
 */
export async function exportData({ userId, tableType, format }) {
  const query = new URLSearchParams({ userId, tableType, format })

  try {
    const saved = await download(`/UserDetailsAdmin/Export?${query}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function downloadSurveyReport({ userId, surveyId }) {
  const query = new URLSearchParams({ userId, surveyId: String(surveyId) })

  try {
    const saved = await download(`/UserDetailsAdmin/DownloadSurveyReport?${query}`)
    return { ok: true, filename: saved.filename }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
